import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  const [parallaxY, setParallaxY] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = window.innerHeight;
      if (scrollY <= maxScroll) {
        setParallaxY(scrollY * 0.18);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative w-full min-h-[100dvh] overflow-hidden flex items-center bg-foreground-950"
    >
      {/* Real founder / salon photo as full background — stays STATIC */}
      <div
        className="absolute inset-0 w-full h-[118%]"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <img
          src="https://storage.readdy-site.link/project_files/0232c479-757c-417f-9996-0a4bc3a6e43d/ddb48840-fb52-4ee5-bdd4-f577013073eb_compressed_brigitte.webp"
          alt="Mme Ahui Brigitte, fondatrice de KD'S BEAUTY, dans son salon à Angré Château"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/55 via-foreground-950/30 to-foreground-950/5" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 via-transparent to-foreground-950/15" />

      {/* Floating "open today" badge */}
      <div className="absolute top-24 right-4 md:right-8 z-20 hidden md:block">
        <div className="bg-background-50/10 backdrop-blur-md border border-background-50/20 rounded-2xl px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-background-50/90 text-xs font-medium">Ouvert aujourd'hui</span>
          </div>
          <p className="text-background-50/60 text-xs mt-1">09h00 — 19h00</p>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div
          className={`max-w-2xl transition-all duration-1000 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 mb-6 md:mb-8">
            <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
            <span className="text-background-50/90 text-xs md:text-sm tracking-[0.15em] uppercase font-label">
              Spa Beauté & Soins — Angré Château
            </span>
          </div>

          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-background-50 font-light leading-[1.05] mb-5 md:mb-7">
            {t('hero.title')}
          </h1>
          <p className="text-background-50/85 text-base md:text-lg lg:text-xl font-body max-w-xl mb-9 md:mb-11 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* Founder signature line */}
          <div className="flex items-center gap-3 mb-9 md:mb-11">
            <div className="w-12 h-12 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center border border-background-50/40 shrink-0">
              <img
                src="https://static.readdy.ai/image/66108e401cf688a02511c33e687ef000/027bb56ccb9ee3af4b09b48629b253d4.png"
                alt="Logo KD'S BEAUTY"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-background-50">Mme Ahui Brigitte</p>
              <p className="text-xs text-background-50/70">Fondatrice & Esthéticienne diplômée</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link
              to="/rdv"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base md:text-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-all duration-300 whitespace-nowrap shadow-lg shadow-green-600/25"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg" />
              </div>
              {t('hero.cta')}
            </Link>
            <Link
              to="/soins"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full text-base md:text-lg font-medium border-2 border-background-50 text-background-50 hover:bg-background-50 hover:text-foreground-800 transition-all duration-300 whitespace-nowrap"
            >
              {t('hero.discover')}
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-background-50/50 text-xs tracking-[0.2em] uppercase font-label">Découvrir</span>
        <div className="w-px h-10 bg-gradient-to-b from-primary-300 to-transparent animate-pulse" />
      </div>
    </section>
  );
}